import React, { useState, useEffect } from 'react';
import { useStore } from '../../store/StoreContext';
import { useToast } from '../../hooks/useToast';
import { IconPlus, IconTrash } from '../../components/icons';
import { NumberField, ChoiceKeys, Button, IconButton } from '../../components/ui';

const TRUCK_OPTIONS = [
    { value: 'none', label: 'Labor Only' },
    { value: '16ft', label: "16' Truck" },
    { value: '20ft', label: "20' Truck" }, // 2026-09-11: 20' has been a real size since May; without a row here every 20' estimate borrowed the crew's other-truck rate
    { value: '26ft', label: "26' Truck" },
    // '2x26ft' retired 2026-09-11 — a second truck is an EXTRA (per-hour fee below), not a rate row.
];

// Crew sizes offered as keys (form-controls.md: short lists are keys). A
// saved row outside this range still shows as an extra key so it isn't
// silently reassigned.
const CREW_SIZES = [2, 3, 4, 5, 6, 7, 8];
const crewItems = (current) => {
    const sizes = CREW_SIZES.includes(current) || current == null ? CREW_SIZES : [current, ...CREW_SIZES].sort((a, b) => a - b);
    return sizes.map(n => ({ value: n, label: `${n} ${n === 1 ? 'guy' : 'guys'}` }));
};

const SERVICE_TYPES = [
    { value: 'moving', label: 'Moving' },
    { value: 'packing', label: 'Packing' },
    { value: 'loading', label: 'Loading Only' },
    { value: 'unloading', label: 'Unloading Only' },
];

const DEFAULT_RATES = [
    { crew_size: 2, truck_type: 'none', service_type: 'moving', hourly_rate: 200 },
    { crew_size: 2, truck_type: '16ft', service_type: 'moving', hourly_rate: 185 },
    { crew_size: 2, truck_type: '26ft', service_type: 'moving', hourly_rate: 200 },
    { crew_size: 3, truck_type: '26ft', service_type: 'moving', hourly_rate: 235 },
    { crew_size: 4, truck_type: '26ft', service_type: 'moving', hourly_rate: 275 },
    { crew_size: 6, truck_type: '26ft', service_type: 'moving', hourly_rate: 350 },
    { crew_size: 2, truck_type: 'none', service_type: 'packing', hourly_rate: 125 },
];

function RateTableManager() {
    const { config, saveConfig } = useStore();
    const { show: showToast } = useToast();
    const [expanded, setExpanded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [extraTruckFee, setExtraTruckFee] = useState(() => {
        try { return JSON.parse(config.extra_truck_fee || '25'); } catch { return 25; }
    });
    const [heavyItemFee, setHeavyItemFee] = useState(() => {
        try { return JSON.parse(config.heavy_item_fee || '200'); } catch { return 200; }
    });

    const [rows, setRows] = useState(() => {
        try {
            const parsed = JSON.parse(config.rate_table || '[]');
            return parsed.length > 0 ? parsed : DEFAULT_RATES;
        } catch { return DEFAULT_RATES; }
    });

    useEffect(() => {
        try {
            const parsed = JSON.parse(config.rate_table || '[]');
            setRows(parsed.length > 0 ? parsed : DEFAULT_RATES);
        } catch { setRows(DEFAULT_RATES); }
        try { setExtraTruckFee(JSON.parse(config.extra_truck_fee || '25')); } catch { setExtraTruckFee(25); }
        try { setHeavyItemFee(JSON.parse(config.heavy_item_fee || '200')); } catch { setHeavyItemFee(200); }
    }, [config.rate_table, config.extra_truck_fee, config.heavy_item_fee]);

    const addRow = () => setRows(prev => [...prev, { crew_size: 2, truck_type: 'none', service_type: 'moving', hourly_rate: 200 }]);
    const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));
    const updateRow = (idx, field, value) => setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

    const saveTable = async () => {
        setSaving(true);
        try {
            await saveConfig('rate_table', JSON.stringify(rows));
            await saveConfig('extra_truck_fee', JSON.stringify(extraTruckFee));
            await saveConfig('heavy_item_fee', JSON.stringify(heavyItemFee));
            showToast('Rate table saved');
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        } finally {
            setSaving(false);
        }
    };

    // Group rows by service type for display
    const movingRows = rows.map((r, i) => ({ ...r, _idx: i })).filter(r => r.service_type !== 'packing');
    const packingRows = rows.map((r, i) => ({ ...r, _idx: i })).filter(r => r.service_type === 'packing');

    // Each rate row is a wrapping band of key groups (crew / truck /
    // service) plus the rate field — keys need more room than the old
    // 12-column select grid gave them, so each group carries its own label.
    const groupLabel = 'block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1';
    const renderRow = (row) => (
        <div key={row._idx} className="flex flex-wrap items-end gap-x-4 gap-y-2 bg-slate-50 rounded-lg p-2.5">
            <div>
                <span className={groupLabel}>Crew</span>
                <ChoiceKeys size="sm" items={crewItems(row.crew_size)} value={row.crew_size}
                    onChange={v => updateRow(row._idx, 'crew_size', parseInt(v))} ariaLabel="Crew size" />
            </div>
            <div>
                <span className={groupLabel}>Truck</span>
                <ChoiceKeys size="sm" items={TRUCK_OPTIONS} value={row.truck_type}
                    onChange={v => updateRow(row._idx, 'truck_type', v)} ariaLabel="Truck" />
            </div>
            <div>
                <span className={groupLabel}>Service</span>
                <ChoiceKeys size="sm" items={SERVICE_TYPES} value={row.service_type}
                    onChange={v => updateRow(row._idx, 'service_type', v)} ariaLabel="Service" />
            </div>
            <div>
                <span className={groupLabel}>Rate</span>
                <NumberField size="sm" step={5} min={0} prefix="$" suffix="/hr" className="w-[150px]" inputClassName="font-semibold"
                    value={row.hourly_rate}
                    onChange={v => updateRow(row._idx, 'hourly_rate', parseFloat(v) || 0)} ariaLabel="Hourly rate" />
            </div>
            <div className="flex items-center self-center ml-auto">
                <IconButton size="sm" tone="danger" label="Remove rate" onClick={() => removeRow(row._idx)}><IconTrash size={14} /></IconButton>
            </div>
        </div>
    );

    return (
        <div className="bg-white rounded-xl border border-slate-200">
            {/* Collapsible Header */}
            <div className="flex items-center justify-between p-6 cursor-pointer select-none" onClick={() => setExpanded(!expanded)}>
                <div className="flex items-center gap-3">
                    <span className={`text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`}>&#9654;</span>
                    <div>
                        <h2 className="text-lg font-bold text-slate-900">Rate Table</h2>
                        {!expanded && <p className="text-xs text-slate-400 mt-0.5">{rows.length} rate{rows.length !== 1 ? 's' : ''} configured · Extra truck +${extraTruckFee}/hr · Heavy item {heavyItemFee > 0 ? '$' + heavyItemFee : 'N/A'}</p>}
                        {expanded && <p className="text-xs text-slate-500 mt-0.5">Crew size + truck configuration = hourly rate. These rates auto-fill on new estimates.</p>}
                    </div>
                </div>
                {expanded && (
                    <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); addRow(); }} leftIcon={<IconPlus size={13} />}>Add Rate</Button>
                )}
            </div>

            {!expanded ? null : <div className="px-6 pb-6">
            {/* Surcharges & Fees */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5 mt-4">
                <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <span className="text-sm text-amber-800 font-medium whitespace-nowrap">Extra truck:</span>
                    <NumberField size="sm" step={5} min={0} prefix="$" className="w-[140px] flex-shrink-0" inputClassName="font-semibold"
                        value={extraTruckFee}
                        onChange={v => setExtraTruckFee(parseFloat(v) || 0)} ariaLabel="Extra truck fee per hour" />
                    <span className="text-xs text-amber-600">/hr per additional truck</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-rose-50 border border-rose-200 rounded-lg">
                    <span className="text-sm text-rose-800 font-medium whitespace-nowrap">Heavy item fee:</span>
                    <NumberField size="sm" step={5} min={0} prefix="$" className="w-[140px] flex-shrink-0" inputClassName="font-semibold"
                        value={heavyItemFee}
                        onChange={v => setHeavyItemFee(parseFloat(v) || 0)} ariaLabel="Heavy item fee" />
                    <span className="text-xs text-rose-600">flat fee per heavy item</span>
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="text-center py-6 text-slate-400">
                    <p className="text-sm">No rates configured yet</p>
                    <p className="text-xs mt-1">Add rates to auto-fill pricing on estimates</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Moving rates */}
                    {movingRows.length > 0 && (
                        <div>
                            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Moving / Labor</div>
                            <div className="space-y-2">
                                {movingRows.map(renderRow)}
                            </div>
                        </div>
                    )}

                    {/* Packing rates */}
                    {packingRows.length > 0 && (
                        <div>
                            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Packing</div>
                            <div className="space-y-2">
                                {packingRows.map(renderRow)}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
                <p className="text-[11px] text-slate-400">Rates are matched automatically when building estimates based on crew size and truck selection.</p>
                <Button variant="primary" onClick={saveTable} loading={saving}>Save Rate Table</Button>
            </div>

            </div>}
        </div>
    );
}

export default RateTableManager;
